import { Box, Flex, Link } from '@chakra-ui/react';
import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { isTabAllowed, useGetLoggedInUser } from '@revelio/auth';
import { FeatureFlag, TourClasses } from '@revelio/core';

import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import { type TabItem, useTabOrder } from '../hooks/useTabOrder';
import {
  ANALYTICS_VIEWS,
  type AnalyticsEntity,
} from '../utils/revelio-primary-filter-entity-switch';
import { DraggableTabLink } from './DraggableTabLink';
import {
  JustDraggedProvider,
  type JustDraggedRef,
} from './DraggableTabLinkContext';
import { PagesMenu } from './PagesMenu';
import {
  formatTransform,
  type IndicatorRect,
  measureLabelRect,
  TAB_HIT_PADDING_X,
} from './tab-indicator';

const GRABBING_STYLE_ID = 'tab-bar-grabbing-cursor';

// Persists across route-level remounts so the indicator can slide-animate from
// its previous position when the Header re-renders for a new tab's page.
let lastIndicatorRect: IndicatorRect | null = null;

export const AnalyticsViewTabs = ({ entity }: { entity: AnalyticsEntity }) => {
  const isDraggablePagesTabsEnabled = useUnleashFlag(
    FeatureFlag.DraggablePagesTabs
  );
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);

  // Use the draggable unified tab bar whenever DashboardNewLayout is on,
  // even if DraggablePagesTabs hasn't been toggled yet. DashboardNewLayout
  // hides CustomDashSubNav (the sidebar page list), so the unified tab bar is
  // the only surface for custom-page navigation and creation in that mode.
  if (!isDraggablePagesTabsEnabled && !isNewDashboardLayout) {
    return <StaticAnalyticsViewTabs entity={entity} />;
  }

  return <DraggableAnalyticsViewTabs entity={entity} />;
};

const StaticAnalyticsViewTabs = ({ entity }: { entity: AnalyticsEntity }) => {
  const location = useLocation();
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const { loggedInUser } = useGetLoggedInUser();

  const visibleViews = ANALYTICS_VIEWS.filter((v) =>
    isTabAllowed(v.serverTab, loggedInUser?.tabs ?? [])
  );

  const activeIndex = visibleViews.findIndex(
    (view) => location.pathname === `/${view.paths[entity]}`
  );

  const slideIndicatorTo = useCallback(
    (tab: HTMLAnchorElement | null | undefined) => {
      const el = indicatorRef.current;
      if (!tab || !el) return;
      const next = measureLabelRect(tab);
      el.style.opacity = '1';
      el.style.transform = formatTransform(next);
      lastIndicatorRect = next;
    },
    []
  );

  useLayoutEffect(() => {
    const el = indicatorRef.current;
    if (!el) return;
    if (activeIndex < 0) {
      el.style.opacity = '0';
      lastIndicatorRect = null;
      return;
    }
    const raf = requestAnimationFrame(() => {
      slideIndicatorTo(tabRefs.current[activeIndex]);
    });
    return () => cancelAnimationFrame(raf);
  }, [activeIndex, slideIndicatorTo]);

  return (
    <Flex
      as="nav"
      aria-label="Analytics views"
      className={TourClasses.TOUR_ANALYTICS_VIEW_TABS_CLASS}
      data-testid="analytics-view-tabs"
      alignItems="stretch"
      gap={1}
      height="32px"
      position="relative"
    >
      {visibleViews.map((view, i) => {
        const path = `/${view.paths[entity]}${location.search}`;
        const isActive = activeIndex === i;
        return (
          <Link
            key={view.name}
            ref={(el: HTMLAnchorElement | null) => {
              tabRefs.current[i] = el;
            }}
            as={NavLink}
            to={path}
            onClick={() => slideIndicatorTo(tabRefs.current[i])}
            data-testid={`analytics-view-tab-${view.name.toLowerCase().replace(/\s+/g, '-')}`}
            display="flex"
            alignItems="center"
            height="48px"
            my="-8px"
            px={`${TAB_HIT_PADDING_X}px`}
            fontSize="14px"
            fontWeight="600"
            color={isActive ? 'text.primary' : '#8F9CB2'}
            whiteSpace="nowrap"
            _hover={{ color: 'text.primary', textDecoration: 'none' }}
          >
            {view.name}
          </Link>
        );
      })}
      <div
        ref={indicatorRef}
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-1px',
          left: 0,
          width: '1px',
          height: '2px',
          background: 'var(--chakra-colors-green-500)',
          transform: lastIndicatorRect
            ? formatTransform(lastIndicatorRect)
            : 'translate3d(0, 0, 0) scaleX(0)',
          transformOrigin: 'left center',
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
          pointerEvents: 'none',
          opacity: lastIndicatorRect ? 1 : 0,
        }}
      />
    </Flex>
  );
};

const DraggableAnalyticsViewTabs = ({
  entity,
}: {
  entity: AnalyticsEntity;
}) => {
  const location = useLocation();
  const tabRefs = useRef<Map<string, HTMLAnchorElement | null>>(new Map());
  const indicatorRef = useRef<HTMLDivElement>(null);

  const {
    orderedTabs,
    barTabs,
    menuTabs,
    previewMove,
    saveOrder,
    commitDropOnMenu,
    cancelPreview,
  } = useTabOrder(entity);
  const [activeTab, setActiveTab] = useState<TabItem | null>(null);
  const justDraggedRef: JustDraggedRef = useRef<string | null>(null);

  const isDragging = activeTab !== null;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } })
  );

  const activeBarTab = barTabs.find((t) => t.path === location.pathname);
  const activeMenuTab = menuTabs.find((t) => t.path === location.pathname);
  // Tab object identity is stable across reorders (same ref, new slot), so the
  // indicator effect needs the position as a separate dep to re-slide.
  const activeBarIdx = activeBarTab
    ? barTabs.findIndex((t) => t.id === activeBarTab.id)
    : -1;

  const slideIndicatorTo = useCallback(
    (tab: HTMLAnchorElement | null | undefined) => {
      const el = indicatorRef.current;
      if (!tab || !el) return;
      const next = measureLabelRect(tab);
      el.style.opacity = '1';
      el.style.transform = formatTransform(next);
      lastIndicatorRect = next;
    },
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeBarIdx is a re-run trigger — activeBarTab's object identity is stable across reorders, so position changes need this extra signal to re-slide.
  useLayoutEffect(() => {
    const el = indicatorRef.current;
    if (!el) return;
    // During drag, sortable transforms move tabs but offsetLeft stays at the
    // pre-drag position, so any slide would point at the wrong slot. Hide the
    // indicator until the drag commits and the bar re-renders in final order.
    if (isDragging) {
      el.style.opacity = '0';
      return;
    }
    if (!activeBarTab) {
      el.style.opacity = '0';
      lastIndicatorRect = null;
      return;
    }
    // rAF ensures the browser paints the cached "from" transform once before
    // we mutate to the target — without this both transforms flush in one
    // paint and CSS has nothing to animate from. Animating `transform` keeps
    // the slide on the compositor so chart-loading work can't drop frames.
    const raf = requestAnimationFrame(() => {
      slideIndicatorTo(tabRefs.current.get(activeBarTab.id) ?? null);
    });
    return () => cancelAnimationFrame(raf);
  }, [activeBarTab, activeBarIdx, slideIndicatorTo, isDragging]);

  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      if (!over || active.id === over.id) return;
      previewMove(String(active.id), String(over.id));
    },
    [previewMove]
  );

  const setGrabbingCursor = useCallback(() => {
    if (document.getElementById(GRABBING_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = GRABBING_STYLE_ID;
    // Universal selector + !important is the only way to beat per-element
    // cursors (anchors, buttons, inputs) during a drag.
    style.textContent = `*, *::before, *::after { cursor: grabbing !important; }`;
    document.head.appendChild(style);
  }, []);

  const clearGrabbingCursor = useCallback(() => {
    document.getElementById(GRABBING_STYLE_ID)?.remove();
  }, []);

  // Tag the just-dragged tab id for a brief window so its NavLink can ignore
  // the synthetic click browsers fire on mouseup. Outside-click handlers
  // (e.g. the popover's close-on-outside-click) are untouched.
  const markJustDragged = useCallback((id: string) => {
    justDraggedRef.current = id;
    setTimeout(() => {
      if (justDraggedRef.current === id) justDraggedRef.current = null;
    }, 100);
  }, []);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const id = String(active.id);
      const tab = orderedTabs.find((t) => t.id === id) ?? null;
      setGrabbingCursor();
      setActiveTab(tab);
      markJustDragged(id);
    },
    [setGrabbingCursor, markJustDragged, orderedTabs]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      clearGrabbingCursor();
      setActiveTab(null);
      markJustDragged(String(active.id));
      if (over?.id === 'menu') {
        commitDropOnMenu(String(active.id));
        return;
      }
      saveOrder();
    },
    [clearGrabbingCursor, saveOrder, commitDropOnMenu, markJustDragged]
  );

  const handleDragCancel = useCallback(() => {
    clearGrabbingCursor();
    setActiveTab(null);
    cancelPreview();
  }, [clearGrabbingCursor, cancelPreview]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <JustDraggedProvider value={justDraggedRef}>
        <Flex
          as="nav"
          aria-label="Analytics views"
          className={TourClasses.TOUR_ANALYTICS_VIEW_TABS_CLASS}
          data-testid="analytics-view-tabs"
          alignItems="stretch"
          gap={1}
          height="32px"
          position="relative"
        >
          <SortableContext
            items={barTabs.map((t) => t.id)}
            strategy={horizontalListSortingStrategy}
          >
            {barTabs.map((tab) => (
              <DraggableTabLink
                key={tab.id}
                tab={tab}
                variant="bar"
                isActive={activeBarTab?.id === tab.id}
                tabRef={(el) => tabRefs.current.set(tab.id, el)}
                onNavigate={() =>
                  slideIndicatorTo(tabRefs.current.get(tab.id) ?? null)
                }
                testId={`analytics-view-tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
              />
            ))}
          </SortableContext>
          <div
            ref={indicatorRef}
            aria-hidden
            style={{
              position: 'absolute',
              bottom: '-1px',
              left: 0,
              width: '1px',
              height: '2px',
              background: 'var(--chakra-colors-green-500)',
              transform: lastIndicatorRect
                ? formatTransform(lastIndicatorRect)
                : 'translate3d(0, 0, 0) scaleX(0)',
              transformOrigin: 'left center',
              transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
              willChange: 'transform',
              pointerEvents: 'none',
              opacity: lastIndicatorRect ? 1 : 0,
            }}
          />
          <PagesMenu
            entity={entity}
            menuTabs={menuTabs}
            activeMenuTabId={activeMenuTab?.id ?? null}
          />
        </Flex>
      </JustDraggedProvider>
      <DragOverlay dropAnimation={null}>
        {activeTab ? <TabGhost label={activeTab.label} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

const TabGhost = ({ label }: { label: string }) => (
  <Box
    display="inline-flex"
    alignItems="center"
    height="32px"
    fontSize="14px"
    fontWeight="600"
    color="text.primary"
    whiteSpace="nowrap"
    cursor="grabbing"
  >
    {label}
  </Box>
);
